import React from "react"
import { DckActionCreators, DckSelectors } from "dck-redux"
import PropTypes from "prop-types"
import {
  Alert,
  Button,
  ButtonToolbar,
  Col,
  ControlLabel,
  Form,
  FormControl,
  FormGroup,
  Row,
  ToggleButton,
  ToggleButtonGroup
} from "react-bootstrap"
import FontAwesome from "react-fontawesome"
import { connect } from "react-redux"
import { blockingFetchService } from "../../lib/blockingFetch"
import {
  capitalFirstCharacter,
  DATE_FORMAT_STR,
  downloadBlob,
  getReportFileName,
  ReportAPI,
  sortBatches,
  sortBuckets,
  sortBundles,
  sortBusinesses,
  sortGLAccounts,
  validateTextState,
  VALIDATION_TYPE
} from "../../lib/utils"
import * as ItemTypes from "../../redux/items/types"
import moment from "moment"
import FieldGroup from "../fragments/FieldGroup"
import Authenticated from "../../lib/Authenticated"
import AccountingReportLinks from "./AccountingReportLinks"
import CustomBreadCrumb from "../fragments/CustomBreadCrumb"
import LoanReportLinks from "./LoanReportLinks"
import FinancierReportLinks from "./financiers/FinancierReportLinks"
import PdfPreviewer from "./PdfPreviewer"
import ReportRequest from "./ReportRequest"
import reduxStore from '../../store'
import { addOrUpdateDownloadRequest } from '../../redux/downloadRequests'
import { createToast } from "../../redux/actions/toastNotifications"
import GenerationHelpPanel from "./GenerationHelpPanel"

const ReportTypeTab = [
  'SUMMARY', 
  'DETAILS'
],

ReportOutputTab = [
  ['HTML', 'Normal format'], 
  ['XLS', 'Excel format'], 
  ['XLSX', 'Excel 2007 format'], 
  ['CSV', 'CSV format'], 
  ['PDF', 'PDF format']
],

GeneralityType = {
  ACTIVE: 'ACTIVE',
  FULLY_PAID: 'FULLY_PAID',
  ALL: 'ALL'
},

GeneralityTypeTab = [
  [ GeneralityType.ACTIVE, 'Active Loans' ],
  [ GeneralityType.FULLY_PAID, 'Fully Repaid Loans' ],
  [ GeneralityType.ALL, 'Active + Fully Repaid Loans' ]
]

class ReportForm extends React.Component {

  state = {
    reportType: ReportTypeTab[0],
    generalityType: GeneralityType.ALL,
    outputType: 'PDF',
    officeId: 1,
    groupId: -1,
    loanProductId: -1,
    startDate: moment().startOf('month').format(DATE_FORMAT_STR),
    endDate: moment().endOf('month').format(DATE_FORMAT_STR),
    onDate: moment().endOf('month').format(DATE_FORMAT_STR),
    glAccountId: -1,
    batchId: -1,
    bucketId: -1,

    outputTypeValidationState: VALIDATION_TYPE.SUCCESS,
    reportTypeValidationState: VALIDATION_TYPE.SUCCESS,
    officeIdValidationState: VALIDATION_TYPE.SUCCESS,
    loanProductIdValidationState: VALIDATION_TYPE.SUCCESS,
    startDateValidationState: VALIDATION_TYPE.SUCCESS,
    endDateValidationState: VALIDATION_TYPE.SUCCESS,
    onDateValidationState: VALIDATION_TYPE.SUCCESS,
    generalityTypeValidationState: VALIDATION_TYPE.SUCCESS,
    glAccountIdValidationState: VALIDATION_TYPE.SUCCESS,
    batchIdValidationState: VALIDATION_TYPE.SUCCESS,
    bucketIdValidationState: VALIDATION_TYPE.SUCCESS,

    running: false,
    error: null,
    errorStatus: 0,
    reportSection: 1,

    isModalOpen: false,
    pdfBlob: null,
    pdfUrl: null,
    progress: 0,
    showGenerationHelp: false,
  }

  progressInterval = null

  authenticated = new Authenticated()
  isSuper = this.authenticated.isSuper()
  isPaymentManager = this.authenticated.isPaymentManager()

  /**
   * Loading additional data
   */
  componentDidMount() {

    const { reportAPI, loadBundles, loadOrganizations, loadBusinesses, loadGLAccounts,
      loadBatches, loadBuckets } = this.props

    document.addEventListener('keydown', this.handleKeyDown)

    if(this.isSuper) {
      loadOrganizations()
    }

    if (reportAPI === ReportAPI.ACTIVE_LOAN || reportAPI === ReportAPI.PORTFOLIO_AT_RISK) {
      loadBundles()
    }
    else if (reportAPI === ReportAPI.EXPECTED_PAYMENT || reportAPI === ReportAPI.LOAN_DISBURSED
      || reportAPI === ReportAPI.DEFAULTED_LOAN_DETAILS) {
      loadBusinesses()
    }
    else if (reportAPI === ReportAPI.GENERAL_LEDGER) {
      loadGLAccounts()
    }
    else if (reportAPI === ReportAPI.BATCH_PERFORMANCE) {
      loadBusinesses()
      loadBatches()
    }
    else if (reportAPI === ReportAPI.BUCKET_PERFORMANCE) {
      loadBuckets()
    }

    this.setReportSection(reportAPI)
  }

  componentWillUnmount() {
    document.removeEventListener('keydown', this.handleKeyDown)
    if (this.state.pdfUrl) {
      URL.revokeObjectURL(this.state.pdfUrl)
    }
    if (this.progressInterval) {
      clearInterval(this.progressInterval)
    }
  }

  setReportSection = reportAPI => {

    let section = 1

    switch(reportAPI) {

      case ReportAPI.BATCH_PERFORMANCE:
      case ReportAPI.BUCKET_PERFORMANCE:
        section = 3
        break
  
      case ReportAPI.GENERAL_LEDGER:
      case ReportAPI.INCOME_STATEMENT:
      case ReportAPI.BALANCE_SHEET:
      case ReportAPI.TRIAL_BALANCE:
        section = 2
        break

      default: section = 1
    }

    this.setState({ reportSection: section })
  }

  getTitleName = (reportType, generalityType) => {

    const { reportAPI } = this.props,
    reportTypeStr = capitalFirstCharacter(reportType)

    switch(reportAPI) {
      case ReportAPI.ACTIVE_LOAN:
        return `Active Loans ${ reportTypeStr }`

      case ReportAPI.AGING_LOAN:
        return 'Aging Loans'

      case ReportAPI.BALANCE_OUTSTANDING:
        return 'Balance Outstanding'

      case ReportAPI.EXPECTED_PAYMENT:
        return 'Expected Payments By Date'

      case ReportAPI.PORTFOLIO_AT_RISK:
        return 'Portfolio At Risk'

      case ReportAPI.BRANCH_CASH_IN:
        return `Branch Cash In ${ reportTypeStr }`

      case ReportAPI.LOAN_GENERALITY:
        return generalityType === GeneralityType.ACTIVE
          ? 'Active Loans'
          : generalityType === GeneralityType.FULLY_PAID
            ? 'Fully Repaid Loans'
            : 'Active + Fully Repaid Loans'

      case ReportAPI.LOAN_DISBURSED:
        return 'Loans Disbursed By Affiliation'

      case ReportAPI.GENERAL_LEDGER:
        return `General Ledger Report`

      case ReportAPI.INCOME_STATEMENT:
        return `Income Statement`

      case ReportAPI.BALANCE_SHEET:
        return `Balance Sheet`

      case ReportAPI.TRIAL_BALANCE:
        return `Trial Balance`

      case ReportAPI.BATCH_PERFORMANCE:
        return `Batch Performance ${ reportTypeStr }`

      case ReportAPI.BUCKET_PERFORMANCE:
        return `Bucket Performance ${ reportTypeStr }`

      case ReportAPI.DEFAULTED_LOAN_DETAILS:
        return `Defaulted Loans`

      default: return ''
    }
  }

  reportSectionChanged = value => this.setState({ reportSection: value })

  outputTypeChanged = outputType => this.setState({
    outputType,
    outputTypeValidationState: validateTextState(outputType, false),
    error: null,
  })

  reportTypeChanged = reportType => this.setState({
    reportType,
    reportTypeValidationState: validateTextState(reportType, false),
    error: null,
  })

  officeIdChanged = officeId => this.setState({
    officeId,
    officeIdValidationState: validateTextState(officeId, false),
    groupId: -1,
    loanProductId: -1,
    loanProductIdValidationState: VALIDATION_TYPE.SUCCESS,
    glAccountId: -1,
    glAccountIdValidationState: VALIDATION_TYPE.SUCCESS,
    batchId: -1,
    batchIdValidationState: VALIDATION_TYPE.SUCCESS,
    bucketId: -1,
    bucketIdValidationState: VALIDATION_TYPE.SUCCESS,
    error: null,
  })

  loanProductIdChanged = loanProductId => this.setState({
    loanProductId,
    loanProductIdValidationState: validateTextState(loanProductId, false),
    error: null,
  })

  startDateChanged = startDate => this.setState({
    startDate,
    startDateValidationState: validateTextState(startDate, false),
    error: null,
  })

  endDateChanged = endDate => this.setState({
    endDate,
    endDateValidationState: validateTextState(endDate, false),
    error: null,
  })

  onDateChanged = onDate => this.setState({
    onDate,
    onDateValidationState: validateTextState(onDate, false),
    error: null,
  })

  groupIdChanged = groupId => this.setState({
    groupId,
    batchId: -1,
    batchIdValidationState: VALIDATION_TYPE.SUCCESS,
    error: null,
  })

  generalityTypeChanged = generalityType => this.setState({
    generalityType,
    generalityTypeValidationState: validateTextState(generalityType, false),
    error: null,
  })

  glAccountIdChanged = glAccountId => this.setState({
    glAccountId,
    glAccountIdValidationState: validateTextState(glAccountId, false),
    error: null,
  })

  batchIdChanged = batchId => this.setState({
    batchId,
    batchIdValidationState: validateTextState(batchId, false),
    error: null,
  })

  bucketIdChanged = bucketId => this.setState({
    bucketId,
    bucketIdValidationState: validateTextState(bucketId, false),
    error: null,
  })

  isValid = () => {

    const {
      reportTypeValidationState, outputTypeValidationState, officeIdValidationState,
      loanProductIdValidationState, startDateValidationState, endDateValidationState,
      onDateValidationState, generalityTypeValidationState, glAccountIdValidationState,
      batchIdValidationState, bucketIdValidationState } = this.state,

    ret = reportTypeValidationState === VALIDATION_TYPE.SUCCESS
      && outputTypeValidationState === VALIDATION_TYPE.SUCCESS
      && officeIdValidationState === VALIDATION_TYPE.SUCCESS
      && loanProductIdValidationState === VALIDATION_TYPE.SUCCESS
      && startDateValidationState === VALIDATION_TYPE.SUCCESS
      && endDateValidationState === VALIDATION_TYPE.SUCCESS
      && onDateValidationState === VALIDATION_TYPE.SUCCESS
      && generalityTypeValidationState === VALIDATION_TYPE.SUCCESS
      && glAccountIdValidationState === VALIDATION_TYPE.SUCCESS
      && batchIdValidationState === VALIDATION_TYPE.SUCCESS
      && bucketIdValidationState === VALIDATION_TYPE.SUCCESS

    return ret
  }

  getApiFacade = (accessToken, isAsync = false) => {

    const { reportType, outputType, officeId, loanProductId, startDate, endDate, onDate,
    groupId, generalityType, glAccountId, batchId, bucketId } = this.state,
    { reportAPI } = this.props,

    request = new ReportRequest(reportAPI, isAsync)
      .setOfficeId(officeId)
      .setOutputType(outputType)
      .setBatchId(batchId)
      .setBucketId(bucketId)
      .setStartDate(startDate)
      .setEndDate(endDate)
      .setOnDate(onDate)
      .setGeneralityType(generalityType)
      .setGlAccountId(glAccountId)
      .setGroupId(groupId)
      .setReportType(reportType)
      .setLoanProductId(loanProductId)

    return request.makeCall(accessToken)
  }

  startProgressSimulation = () => {
    this.setState({ progress: 0 })
    let current = 0
    this.progressInterval = setInterval(() => {
      current += Math.random() * 12 + 3
      if (current >= 92) {
        current = 92
        clearInterval(this.progressInterval)
        this.progressInterval = null
      }
      this.setState({ progress: Math.min(current, 92) })
    }, 300)
  }

  completeProgress = () => {
    if (this.progressInterval) {
      clearInterval(this.progressInterval)
      this.progressInterval = null
    }
    this.setState({ progress: 100 })
  }

  handlePreview = async () => {

    if(!this.isValid()) {
      this.setState({ error: 'Some fields are incorrect.' })
      return
    }

    this.setState({
      isModalOpen: true,
      running: true,
      error: null,
      errorStatus: 0,
      pdfBlob: null,
    })

    if (this.state.pdfUrl) {
      URL.revokeObjectURL(this.state.pdfUrl)
      this.setState({ pdfUrl: null })
    }

    this.startProgressSimulation()

    blockingFetchService(
      accessToken => this.getApiFacade(accessToken), 
      response => {

        this.completeProgress()
        const url = URL.createObjectURL(response)
        this.setState({ pdfBlob: response, pdfUrl: url })
      },
      error => {
        console.error(error)
        this.completeProgress()
        this.setState({ error: error.text, errorStatus: error.status })
      },
      _ => this.setState({ running: false })
    )
  }

  handleKeyDown = event => {
    if (event.key === 'Escape' && this.state.isModalOpen) {
      this.handleClose()
    }
  }

  handleClose = () => {
    if (this.state.pdfUrl) {
      URL.revokeObjectURL(this.state.pdfUrl)
    }
    if (this.progressInterval) {
      clearInterval(this.progressInterval)
      this.progressInterval = null
    }
    this.setState({
      isModalOpen: false,
      pdfBlob: null,
      pdfUrl: null,
      error: null,
      progress: 0,
    })
  }

  handleDownload = () => {
    const { pdfBlob, outputType, reportType, generalityType } = this.state
    if (pdfBlob) {
      const fileName = getReportFileName(
        this.getTitleName(reportType, generalityType), 
        outputType
      )
      downloadBlob(pdfBlob, fileName)
    }
  }

  downloadReport = () => {

    if(!this.isValid()) {
      this.setState({ error: 'Some fields are incorrect.' })
      return
    }

    this.setState({
      running: true,
      error: null,
    })

    blockingFetchService(
      accessToken => this.getApiFacade(accessToken, true),
      response => {
        reduxStore.dispatch(addOrUpdateDownloadRequest(response))
        reduxStore.dispatch(createToast({
          title: 'Report Generation Requested!',
          content: 'Your report is processing in the background. Check the download badge in the top navbar for progress.',
          type: 'SUCCESS',
        }))
      },
      error => this.setState({ error: error.text }),
      _ => this.setState({ running: false })
    )
  }

  /** Render Select Organization */
  renderBrandNameSelect = () => {

    if(!this.isSuper) return null
    const { officeIdValidationState, running, officeId } = this.state,
    { organizations } = this.props

    return (
      <Col xs={6}>
        <FormGroup controlId='officeId' validationState={ officeIdValidationState }>

          <ControlLabel>Organization</ControlLabel>

          <FormControl componentClass="select" placeholder="select"
            value={ officeId } disabled={ running }
            onChange={e => this.officeIdChanged(e.target.value)}>
            <option value="1">All</option>
            { organizations.map(({ id, fineractId, name }) =>
              <option key={ id } value={ fineractId }>{ name }</option>) }
          </FormControl>
        </FormGroup>
      </Col>
    )
  }

  renderAffiliationSelect = () => {

    let { organizations, businesses } = this.props
    const { running, groupId } = this.state
    let { officeId } = this.state
    officeId = Number(officeId)

    if(officeId !== 1) {
      const organization = organizations.find(item => Number(item.fineractId) === officeId)
      if(organization) {
        businesses = businesses.filter(item => item.organizationId === organization.id)
      }
    }

    return (
      <FormGroup controlId='groupId' validationState={ VALIDATION_TYPE.SUCCESS }>

        <ControlLabel>Affiliation</ControlLabel>

        <FormControl componentClass="select" placeholder="select"
          value={ groupId } disabled={ running }
          onChange={e => this.groupIdChanged(e.target.value)}>
          <option value="-1">All</option>
          { businesses.map(({ id, groupId, name, businessId }) =>
            <option key={ id } value={ groupId }>{ name } - { businessId }</option>) }
        </FormControl>
      </FormGroup>
    )
  }

  renderProductSelect = () => {

    let { organizations, bundles } = this.props
    const { running, loanProductIdValidationState, loanProductId } = this.state
    let { officeId } = this.state
    officeId = Number(officeId)

    if(officeId !== 1) {
      const organization = organizations.find(item => Number(item.fineractId) === officeId)
      if(organization) {
        bundles = bundles.filter(item => item.organizationId === organization.id)
      }
    }

    return (
      <FormGroup controlId='loanProductId' validationState={ loanProductIdValidationState }>

        <ControlLabel>Loan bundle</ControlLabel>

        <FormControl componentClass="select" placeholder="select"
          value={ loanProductId } disabled={ running }
          onChange={e => this.loanProductIdChanged(e.target.value)}>
          <option value="-1">All</option>
          { bundles.map(({ id, fineractId, name }) => <option key={ id } value={ fineractId }>{ name }</option>) }
        </FormControl>
      </FormGroup>
    )
  }

  renderStartDateSelect = () => {

    const { startDateValidationState, startDate, running } = this.state

    return (
      <FieldGroup
        id="startDate"
        type="date"
        label="Start Date"
        placeholder=""
        validationState={ startDateValidationState }
        value={ startDate }
        onChange={e => this.startDateChanged(e.target.value)}
        disabled={ running }
      />
    )
  }

  renderEndDateSelect = () => {

    const { endDateValidationState, endDate, running } = this.state

    return (
      <FieldGroup
        id="endDate"
        type="date"
        label="End Date"
        placeholder=""
        validationState={ endDateValidationState }
        value={ endDate }
        onChange={e => this.endDateChanged(e.target.value)}
        disabled={ running }
      />
    )
  }

  renderOnDateSelect = () => {

    const { onDateValidationState, onDate, running } = this.state

    return (
      <FieldGroup
        id="onDate"
        type="date"
        label="On Date"
        placeholder=""
        validationState={ onDateValidationState }
        value={ onDate }
        onChange={e => this.onDateChanged(e.target.value)}
        disabled={ running }
      />
    )
  }

  renderReportType = () => {

    const { reportTypeValidationState, reportType, running } = this.state

    return (
      <FormGroup controlId='reportType' validationState={ reportTypeValidationState }>

        <ControlLabel>Report Type</ControlLabel>

        <ButtonToolbar>
          <ToggleButtonGroup
            onChange={value => this.reportTypeChanged(value)}
            type="radio"
            name="reportType"
            defaultValue={ reportType }>
            { ReportTypeTab.map((value, index) =>
              <ToggleButton key={ index } value={ value } disabled={ running }>
                { value }
              </ToggleButton>
            )}
          </ToggleButtonGroup>
        </ButtonToolbar>
      </FormGroup>
    )
  }

  renderOutputType = () => {

    const { outputTypeValidationState, outputType, running } = this.state

    return (
      <FormGroup controlId='outputType' validationState={ outputTypeValidationState }>

        <ControlLabel>Output Type</ControlLabel>

        <FormControl componentClass="select" placeholder="select"
          value={ outputType } disabled={ running }
          onChange={e => this.outputTypeChanged(e.target.value)}>
          <option value=""></option>
          { ReportOutputTab.map(item => <option key={ item[0] } value={ item[0] }>{ item[1] }</option>) }
        </FormControl>
      </FormGroup>
    )
  }

  renderGeneralityTypeSelect = () => {

    const { generalityTypeValidationState, generalityType, running } = this.state

    return (
      <FormGroup controlId='generalityType' validationState={ generalityTypeValidationState }>

        <ControlLabel>Generality Type</ControlLabel>

        <FormControl componentClass="select" placeholder="select"
          value={ generalityType } disabled={ running }
          onChange={e => this.generalityTypeChanged(e.target.value)}>
          <option value=""></option>
          { GeneralityTypeTab.map(item => <option key={ item[0] } value={ item[0] }>{ item[1] }</option>) }
        </FormControl>
      </FormGroup>
    )
  }

  /** Render select GL account */
  renderGLAccountSelect = () => {

    const { glAccountIdValidationState, running, glAccountId } = this.state
    let { organizations, glAccounts } = this.props,
    { officeId } = this.state

    officeId = Number(officeId)

    if(officeId !== 1) {
      const organization = organizations.find(item => Number(item.fineractId) === officeId)
      if(organization) {
        glAccounts = glAccounts.filter(item => item.organizationId === organization.id)
      }
    }

    return (
      <FormGroup controlId='glAccountId' validationState={ glAccountIdValidationState }>

        <ControlLabel>GL Account</ControlLabel>

        <FormControl componentClass="select" placeholder="select"
          value={ glAccountId } disabled={ running }
          onChange={e => this.glAccountIdChanged(e.target.value)}>
          <option value="-1">All</option>
          { glAccounts.map(({ id, fineractId, name, glCode }) =>
            <option key={ id } value={ fineractId }>{ `${name} (${glCode})` }</option>) }
        </FormControl>
      </FormGroup>
    )
  }

  renderBatchSelect = () => {

    const { batchIdValidationState, running, batchId } = this.state
    let { organizations, batches } = this.props,
    { officeId, groupId } = this.state

    officeId = Number(officeId)
    groupId = Number(groupId)

    if(officeId !== 1) {
      const organization = organizations.find(item => Number(item.fineractId) === officeId)
      if(organization) {
        batches = batches.filter(item => item.organizationId === organization.id)
      }
    }

    if (groupId !== -1) {
      batches = batches.filter(({ affiliation = {} }) => affiliation.groupId === groupId)
    }

    return (
      <FormGroup controlId='batchId' validationState={ batchIdValidationState }>

        <ControlLabel>Batch</ControlLabel>

        <FormControl componentClass="select" placeholder="select"
          value={ batchId } disabled={ running }
          onChange={e => this.batchIdChanged(e.target.value)}>
          <option value="-1">All</option>
          { batches.map(({ id, fineractId, name }) =>
            <option key={ id } value={ fineractId }>{ `${name}` }</option>) }
        </FormControl>
      </FormGroup>
    )
  }

  renderBucketSelect = () => {

    const { bucketIdValidationState, running, bucketId } = this.state
    let { organizations, buckets } = this.props,
    { officeId } = this.state

    officeId = Number(officeId)

    if(officeId !== 1) {
      const organization = organizations.find(item => Number(item.fineractId) === officeId)
      if(organization) {
        buckets = buckets.filter(item => item.organizationId === organization.id)
      }
    }

    return (
      <FormGroup controlId='bucketId' validationState={ bucketIdValidationState }>

        <ControlLabel>Bucket</ControlLabel>

        <FormControl componentClass="select" placeholder="select"
          value={ bucketId } disabled={ running }
          onChange={e => this.bucketIdChanged(e.target.value)}>
          <option value="-1">All</option>
          { buckets.map(({ id, fineractId, name }) =>
            <option key={ id } value={ fineractId }>{ `${name}` }</option>) }
        </FormControl>
      </FormGroup>
    )
  }

  renderCustumFields = reportAPI => {

    switch(reportAPI) {

      case ReportAPI.ACTIVE_LOAN:
        return <Row>
          <Col xs={6}>{ this.renderReportType() }</Col>
          { this.renderBrandNameSelect() }
          <Col xs={6}>{ this.renderProductSelect() }</Col>
          <Col xs={6}>{ this.renderOutputType() }</Col>
        </Row>

      case ReportAPI.AGING_LOAN:
        return <Row>
          { this.renderBrandNameSelect() }
          <Col xs={6}>{ this.renderOutputType() }</Col>
        </Row>

      case ReportAPI.BALANCE_OUTSTANDING:
        return <Row>
          { this.renderBrandNameSelect() }
          <Col xs={6}>{ this.renderOnDateSelect() }</Col>
          <Col xs={6}>{ this.renderOutputType() }</Col>
        </Row>

      case ReportAPI.EXPECTED_PAYMENT:
        return <div>
          <Row>
            <Col xs={6}>{ this.renderStartDateSelect() }</Col>
            <Col xs={6}>{ this.renderEndDateSelect() }</Col>
          </Row>
          <Row>
            { this.renderBrandNameSelect() }
            <Col xs={6}>{ this.renderAffiliationSelect() }</Col>
            <Col xs={6}>{ this.renderOutputType() }</Col>
          </Row>
        </div>

      case ReportAPI.PORTFOLIO_AT_RISK:
        return <Row>
          { this.renderBrandNameSelect() }
          <Col xs={6}>{ this.renderProductSelect() }</Col>
          <Col xs={6}>{ this.renderOutputType() }</Col>
        </Row>

      case ReportAPI.BRANCH_CASH_IN:
        return <div>
          <Row>
            <Col xs={6}>{ this.renderStartDateSelect() }</Col>
            <Col xs={6}>{ this.renderEndDateSelect() }</Col>
          </Row>
          <Row>
            <Col xs={6}>{ this.renderReportType() }</Col>
            { this.renderBrandNameSelect() }
            <Col xs={6}>{ this.renderOutputType() }</Col>
          </Row>
        </div>

      case ReportAPI.LOAN_GENERALITY:
        return <div>
          <Row>
            <Col xs={6}>{ this.renderStartDateSelect() }</Col>
            <Col xs={6}>{ this.renderEndDateSelect() }</Col>
          </Row>
          <Row>
            <Col xs={6}>{ this.renderGeneralityTypeSelect() }</Col>
            { this.renderBrandNameSelect() }
            <Col xs={6}>{ this.renderOutputType() }</Col>
          </Row>
        </div>

      case ReportAPI.LOAN_DISBURSED:
        return <div>
          <Row>
            <Col xs={6}>{ this.renderStartDateSelect() }</Col>
            <Col xs={6}>{ this.renderEndDateSelect() }</Col>
          </Row>
          <Row>
            { this.renderBrandNameSelect() }
            <Col xs={6}>{ this.renderAffiliationSelect() }</Col>
            <Col xs={6}>{ this.renderOutputType() }</Col>
          </Row>
        </div>

      case ReportAPI.GENERAL_LEDGER:
        return <div>
          <Row>
            <Col xs={6}>{ this.renderStartDateSelect() }</Col>
            <Col xs={6}>{ this.renderEndDateSelect() }</Col>
          </Row>
          <Row>
            { this.renderBrandNameSelect() }
            <Col xs={6}>{ this.renderGLAccountSelect() }</Col>
            <Col xs={6}>{ this.renderOutputType() }</Col>
          </Row>
        </div>

      case ReportAPI.INCOME_STATEMENT:
        return <div>
          <Row>
            <Col xs={6}>{ this.renderStartDateSelect() }</Col>
            <Col xs={6}>{ this.renderEndDateSelect() }</Col>
          </Row>
          <Row>
            { this.renderBrandNameSelect() }
            <Col xs={6}>{ this.renderOutputType() }</Col>
          </Row>
        </div>

      case ReportAPI.BALANCE_SHEET:
        return <div>
          <Row>
            <Col xs={6}>{ this.renderEndDateSelect() }</Col>
            { this.renderBrandNameSelect() }
            <Col xs={6}>{ this.renderOutputType() }</Col>
          </Row>
        </div>

      case ReportAPI.TRIAL_BALANCE:
        return <div>
          <Row>
            <Col xs={6}>{ this.renderStartDateSelect() }</Col>
            <Col xs={6}>{ this.renderEndDateSelect() }</Col>
          </Row>
          <Row>
            { this.renderBrandNameSelect() }
            <Col xs={6}>{ this.renderOutputType() }</Col>
          </Row>
        </div>

      case ReportAPI.BATCH_PERFORMANCE:
        return <div>
          <Row>
            <Col xs={6}>{ this.renderStartDateSelect() }</Col>
            <Col xs={6}>{ this.renderEndDateSelect() }</Col>
          </Row>
          <Row>
            <Col xs={6}>{ this.renderReportType() }</Col>
            { this.renderBrandNameSelect() }
            <Col xs={6}>{ this.renderAffiliationSelect() }</Col>
            <Col xs={6}>{ this.renderBatchSelect() }</Col>
            <Col xs={6}>{ this.renderOutputType() }</Col>
          </Row>
        </div>

      case ReportAPI.BUCKET_PERFORMANCE:
        return <div>
          <Row>
            <Col xs={6}>{ this.renderStartDateSelect() }</Col>
            <Col xs={6}>{ this.renderEndDateSelect() }</Col>
          </Row>
          <Row>
            <Col xs={6}>{ this.renderReportType() }</Col>
            { this.renderBrandNameSelect() }
            <Col xs={6}>{ this.renderBucketSelect() }</Col>
            <Col xs={6}>{ this.renderOutputType() }</Col>
          </Row>
        </div>

      case ReportAPI.DEFAULTED_LOAN_DETAILS:
        return <div>
          <Row>
            <Col xs={6}>{ this.renderStartDateSelect() }</Col>
            <Col xs={6}>{ this.renderEndDateSelect() }</Col>
          </Row>
          <Row>
            { this.renderBrandNameSelect() }
            <Col xs={6}>{ this.renderAffiliationSelect() }</Col>
            <Col xs={6}>{ this.renderOutputType() }</Col>
          </Row>
        </div>

      default: return null
    }
  }

  renderMenuTitle = (reportSection, title, isMinusIcons) =>
    <p className="cursor-pointer blue mb-10"
      onClick={() => this.reportSectionChanged(reportSection)}>
      <FontAwesome name={ isMinusIcons ? 'minus' : 'plus' } /> &nbsp; { title }
    </p>

  render() {

    const { error, running, reportType, generalityType, reportSection, outputType,
      isModalOpen, pdfBlob, pdfUrl, progress, errorStatus, showGenerationHelp,
    } = this.state,

    title = this.getTitleName(reportType, generalityType),
    dataLinks = [{ url: '/reports', title: 'Reports' }],
    { reportAPI } = this.props

    return (
      <div>
        <CustomBreadCrumb activeText={ title } data={ dataLinks } />

        <Row className="margin-bottom-40">
          <Col md={4}>
            <div>
              { this.renderMenuTitle(1, 'Loans', reportSection === 1) }
              { reportSection === 1 && <LoanReportLinks api={ reportAPI } /> }
            </div>

            { !this.isPaymentManager &&
            <div>
              { this.renderMenuTitle(2, 'Accounting', reportSection === 2) }
              { reportSection === 2 && <AccountingReportLinks api={ reportAPI } /> }
            </div>
            }

            { !this.isPaymentManager &&
            <div>
              { this.renderMenuTitle(3, 'Financiers', reportSection === 3) }
              { reportSection === 3 && <FinancierReportLinks api={ reportAPI } /> }
            </div>
            }
          </Col>

          <Col md={6}>
            <Form className="simple-form">

              <h4>{ title }</h4>
              { this.renderCustumFields(reportAPI) }

              { error && (
                <Alert bsStyle="danger" onDismiss={() => this.setState({ error: null })}>
                  <strong>{ error }</strong>
                </Alert>
              )}

              <div className="flex justify-end items-center gap-2 mt-10">
                <button
                  type="button"
                  className="border-none p-5 px-10 rounded-6 text-blue-600 bg-transparent"
                  onClick={() => this.setState({ showGenerationHelp: !showGenerationHelp })}
                  title="How report generation works"
                  aria-label="How report generation works"
                >
                  <FontAwesome name="question-circle" size="lg" />
                </button>

                <Button onClick={ this.handlePreview } disabled={ running || outputType !== 'PDF' } className="">
                  <FontAwesome name="eye" />&nbsp; Preview
                </Button>

                <Button onClick={ this.downloadReport } bsStyle="primary" disabled={ running }>
                  { running ? '...' : <FontAwesome name='circle-o'/>}&nbsp; Generate
                </Button>
              </div>

              { showGenerationHelp && <GenerationHelpPanel
                onToggle={() => this.setState({ showGenerationHelp: false })} />}
            </Form>
          </Col>
        </Row>

        <PdfPreviewer
          isModalOpen={ isModalOpen }
          loading={ running }
          error={ error }
          status={ errorStatus }
          pdfBlob={ pdfBlob }
          pdfUrl={ pdfUrl }
          progress={ progress }
          reportTitle={ this.getTitleName(reportType, generalityType) }
          handleDownload={ this.handleDownload }
          handleClose={ this.handleClose }
          handlePreview={ this.handlePreview }
        />
      </div>
    )
  }
}

ReportForm.propTypes = {
  reportAPI: PropTypes.string.isRequired,
}

const mapStateToProps = state => {

  const organizations = DckSelectors.selectAllItems(state, ItemTypes.Organization),

  bundles = DckSelectors.selectAllItems(state, ItemTypes.Bundle)
  sortBundles(bundles)

  let businesses = DckSelectors.selectAllItems(state, ItemTypes.Business)
  businesses = businesses.filter(({ groupId }) => groupId > 0)
  sortBusinesses(businesses)

  let glAccounts = DckSelectors.selectAllItems(state, ItemTypes.GLAccount)
  glAccounts = glAccounts.filter(({ fineractId }) => fineractId > 0)
  sortGLAccounts(glAccounts)

  let batches = DckSelectors.selectAllItems(state, ItemTypes.FinancierBatch)
  batches = batches.filter(({ fineractId }) => fineractId > 0)
  sortBatches(batches)

  let buckets = DckSelectors.selectAllItems(state, ItemTypes.FinancierBucket)
  buckets = buckets.filter(({ fineractId }) => fineractId > 0)
  sortBuckets(buckets)

  return { organizations, bundles, businesses, glAccounts, batches, buckets }
},

mapDispatchToProps = dispatch => ({

  loadOrganizations: () => dispatch(DckActionCreators.itemsLoad(ItemTypes.Organization)),

  loadBundles: () => dispatch(DckActionCreators.itemsLoad(ItemTypes.Bundle)),

  loadBusinesses: () => dispatch(DckActionCreators.itemsLoad(ItemTypes.Business)),

  loadGLAccounts: () => dispatch(DckActionCreators.itemsLoad(ItemTypes.GLAccount)),

  loadBatches: () => dispatch(DckActionCreators.itemsLoad(ItemTypes.FinancierBatch)),

  loadBuckets: () => dispatch(DckActionCreators.itemsLoad(ItemTypes.FinancierBucket)),
})

export default connect(mapStateToProps, mapDispatchToProps)(ReportForm)



// WEBPACK FOOTER //
// ./src/components/Reports/ReportForm.js